import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box
} from '@mui/material';

const CodeVerificationDialog = ({
                                    open,
                                    onClose,
                                    customerId,
                                    onVerified,
                                    // ⬇️ yangi: ranglar
                                    buttonColor,
                                    buttonTextColor,
                                }) => {
    const [codeDigits, setCodeDigits] = useState(['', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

    const handleChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newDigits = [...codeDigits];
            newDigits[index] = value;
            setCodeDigits(newDigits);
            setError('');
            if (value && index < 3) inputRefs[index + 1].current.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
            inputRefs[index - 1].current.focus();
        }
    };

    const handleVerify = async () => {
        const code = codeDigits.join('');
        if (code.length !== 4) {
            setError(t('codeLengthError'));
            return;
        }

        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'https://api.aidex.uz';
            const response = await fetch(
                `${apiUrl}/api/public/verify-code?customerId=${customerId}&code=${code}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );
            const data = await response.json();
            if (data.success) onVerified();
            else setError(data.message || 'Kod noto‘g‘ri yoki muddati tugagan');
        } catch {
            setError(t('verificationFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{t('verificationCode')}</DialogTitle>
            <DialogContent>
                <Typography mb={2}>{t('enterCodeInstruction')}</Typography>

                <Box display="flex" gap={2} justifyContent="center" mb={2}>
                    {codeDigits.map((digit, index) => (
                        <TextField
                            key={index}
                            inputRef={inputRefs[index]}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            inputProps={{
                                maxLength: 1,
                                style: { textAlign: 'center', fontSize: '1.5rem', width: '3rem' }
                            }}
                            error={!!error}
                        />
                    ))}
                </Box>

                {error && (
                    <Typography color="error" variant="body2" textAlign="center">
                        {error}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {/* Cancel — outlined, lekin border va matn rangi buttonColor ga mos */}
                <Button
                    onClick={onClose}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderColor: buttonColor || 'primary.main',
                        color: buttonColor || 'primary.main',
                        '&:hover': {
                            borderColor: buttonColor || 'primary.dark',
                            backgroundColor: 'transparent',
                            opacity: 0.9
                        }
                    }}
                >
                    {t('cancel')}
                </Button>

                {/* Verify — contained, to‘liq rangli */}
                <Button
                    onClick={handleVerify}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: buttonColor || 'primary.main',
                        color: buttonTextColor || '#fff',
                        '&:hover': {
                            backgroundColor: buttonColor || 'primary.dark',
                            opacity: 0.9
                        }
                    }}
                >
                    {loading ? t('verifying') : t('verify')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CodeVerificationDialog;
